/**
 * Used to get the type of the elements in an array that may be undefined
 */
export type ArrayElementType<T> = T extends (infer U)[] ? U : never

/**
 * callback for filter method on array to filter only unique items
 */
export const unique = <T>(value: T, index: number, self: T[]) => {
  return self.indexOf(value) === index
}

/**
 * callback for filter method on array to filter only unique items by a certain value
 */
export const uniqueBy =
  <T>(accessor: (value: T, comparison: T) => boolean) =>
  (value: T, index: number, self: T[]) => {
    return self.findIndex((compareValue) => accessor(value, compareValue)) === index
  }

/**
 * find the union of two arrays, i.e. all unique values from both arrays
 *
 * If you don't want the uniqueness constraint, `concat` is what you want
 *
 * @example
 * const a = [1, 2, 2, 3, 2]
 * const b = [2, 3, 4]
 * union(a, b) // [1, 2, 3, 4]
 * */
export const union = <T>(a: T[], b: T[]) => {
  return [...new Set([...a, ...b])]
}

/**
 * find the intersection of two arrays, i.e. all values that are in both arrays
 *
 * @example
 * const a = [1, 2, 3]
 * const b = [2, 3, 4]
 * intersection(a, b) // [2, 3]
 */
export const intersection = <T>(a: T[], b: T[]) => {
  return a.filter((value) => b.includes(value))
}

/**
 * find the intersection of two arrays, i.e. all values that are in both arrays
 * but using a custom accessor to compare the values. So you can compare objects
 * by a certain property, for example.
 *
 * @example
 * const a = [{ id: 1, name: 'a' }, { id: 2, name: 'b' }]
 * const b = [{ id: 2, name: 'b' }, { id: 3, name: 'c' }]
 * intersectionBy(a, b, (a, b) => a.id === b.id) // [{ id: 2, name: 'b' }]
 *
 */
export const intersectionBy = <T>(
  a: T[],
  b: T[],
  accessor: (value: T, comparison: T) => boolean
) => {
  return a.filter((value) => b.some((compareValue) => accessor(value, compareValue)))
}

/**
 * find the difference of two arrays, i.e. all values that are in the first array
 * but not in the second
 *
 * @example
 * const a = [1, 2, 3]
 * const b = [2, 3, 4]
 * difference(a, b) // [1]
 * */
export const difference = <T>(a: T[], b: T[]) => {
  return a.filter((value) => !b.includes(value))
}

/**
 * find the difference of two arrays, i.e. all values that are in the first array
 * but not in the second, but using a custom accessor to compare the values. So you can compare objects
 * by a certain property, for example.
 *
 * @example
 * const a = [{ id: 1, name: 'a' }, { id: 2, name: 'b' }]
 * const b = [{ id: 2, name: 'b' }, { id: 3, name: 'c' }]
 * differenceBy(a, b, (a, b) => a.id === b.id) // [{ id: 1, name: 'a' }]
 * */
export const differenceBy = <T>(a: T[], b: T[], accessor: (value: T, comparison: T) => boolean) => {
  return a.filter((value) => !b.some((compareValue) => accessor(value, compareValue)))
}

/**
 * The opposite of toKeyedArray, this takes an array of objects with
 * a shared key (the second argument) that is unique to each object.
 * It returns an object with the keys being the values of the shared key
 * and the values being the objects themselves.
 *
 * Be aware that this returns a record with keys of type `string`,
 * so you do not have type safety on accessors.
 * Always check for undefined. Once inside the properties, though,
 * you are guaranteed types match the input type
 *
 * @group Utilities
 *
 * @example
 * const arr = [{ id: 1, name: 'a' }, { id: 2, name: 'b' }]
 * fromKeyedArray(arr, 'id') // { 1: { id: 1, name: 'a' }, 2: { id: 2, name: 'b' } }
 */
export const fromKeyedArray = <Key extends string, Obj extends Record<Key, string | number>>(
  arr: Obj[],
  keyName: Key
): Record<Obj[Key], Obj> => {
  return arr.reduce(
    (acc, next) => {
      const newKey = next[keyName]
      acc[newKey] = next
      return acc
    },
    {} as Record<Obj[Key], Obj>
  )
}

export const randomItem = <T>(arr: readonly T[]): T => {
  return arr[Math.floor(Math.random() * arr.length)]
}

export const weightedRandom = <T>(values: { weight: number; value: T }[]) => {
  const totalWeight = values.reduce((prev, curr) => prev + curr.weight, 0)
  let randomNum = Math.random() * totalWeight

  return values.find((item) => {
    randomNum -= item.weight
    return randomNum <= 0
  })?.value // this is safe because we know that the random number is less than the total weight
}
/**
 * Chunk an array into smaller arrays of a specified size
 */
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  if (chunkSize < 1) {
    throw new Error('Chunk size must be greater than 0')
  }
  if (chunkSize >= array.length) {
    return [array]
  }
  return Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize)
  )
}

/**
 * Returns the element at the specified index in the array.
 * If the index exceeds the length of the array, it wraps around to the beginning.
 */
export function cyclicalLookup<T>(arr: T[], idx: number): T {
  return arr[idx % arr.length]
}

/**
 * Split an array into two arrays based on a predicate function, where
 * the first array is the elements that pass the predicate and the second
 * array is the elements that do not.
 *
 * In other words, equivalent to calling `filter` twice, once with the predicate
 * and once with its negation.
 *
 * @example
 * const arr = [1, 2, 3, 4, 5]
 * const [even, odd] = partition(arr, (n) => n % 2 === 0)
 * console.log(even) // [2, 4]
 * console.log(odd) // [1, 3, 5]
 */
export function partition<T>(arr: T[], predicate: (value: T) => boolean): [T[], T[]] {
  return arr.reduce(
    (acc, next) => {
      if (predicate(next)) {
        acc[0].push(next)
      } else {
        acc[1].push(next)
      }
      return acc
    },
    [[], []] as [T[], T[]]
  )
}

/**
 * Creates an array of a specified length, with each element generated by a provided mapper function.
 *
 * @example
 * // Creates an array of 5 components, each with a unique key
 * const components = times(5, (i) => <Component key={i} />)
 */
export const times = <T>(length: number, mapper: (index: number) => T): T[] =>
  Array.from({ length }, (_, i) => mapper(i))

/**
 * Slice an array from the start until the predicate returns false.
 */
export const takeWhile = <T>(arr: T[], predicate: (value: T) => boolean): T[] => {
  const result: T[] = []
  for (const item of arr) {
    if (!predicate(item)) {
      break
    }
    result.push(item)
  }
  return result
}

export type NonEmptyArray<T> = [T, ...T[]]

export const isNonEmpty = <T>(arr: T[] | undefined): arr is NonEmptyArray<T> => {
  return arr !== undefined && arr.length > 0
}

export const mapFixedLength = <T extends readonly unknown[], U>(
  tuple: T,
  fn: (value: T[number], index: number) => U
): { [K in keyof T]: U } => tuple.map(fn) as { [K in keyof T]: U }

/**
 * Enum to be used to create return values for the compare callback argument to `Array.prototype.sort`
 *
 * @example
 * [1,9,4].sort((a, b) => isCond ? ListOrder.APrecedesB : ListOrder.BPrecedesA)
 */
export enum SortOrder {
  APrecedesB = -1,
  BPrecedesA = 1,
  AEqualsB = 0
}
